import {
  createQuery,
  createSuspenseQuery
} from "../../utils/queryFactories.js";
import { api } from "../../utils/fetch.js";
import { z } from "zod";

const ViewContactSchema = z.object({
  id: z.number(),
  name: z.string().nullish(),
  phone: z.string().nullish(),
  email: z.string().nullish(),
  address: z.string().nullish(),
  notes_text: z.string().nullish(),
  category: z.string().nullish(),
  status: z.string().nullish(),
  tag: z.array(z.string().nullish()),
  updated_by: z.string().nullish(),
  aging: z.string().nullish(),
  updated_at: z.iso.datetime().nullish(),
  linked_to: z.array(z.string().nullish()),
  _group_value: z.string().nullish(),
  _group_count: z.int().nullish()
});

const GetViewContactsSchema = z.array(ViewContactSchema);

const wfRequest = z.object({
  api: z.literal("wf"),
  id: z.string()
});
const publicRequest = z.object({
  api: z.literal("public"),
  id: z.string(),
  slug: z.string()
});

const ViewContactsRequestSchema = z.discriminatedUnion("api", [
  wfRequest,
  publicRequest
]);

const ViewContactsRequestVariables = z.union([
  wfRequest.omit({ api: true }),
  publicRequest.omit({ api: true })
]);

export type ViewContactsRequest = z.infer<typeof ViewContactsRequestSchema>;
export type ViewContactsResponse = z.infer<typeof GetViewContactsSchema>;

const urls = {
  wf: "/views/:id/contacts",
  public: "/public/:slug/boardviews/:id/contacts"
};

export const useViewContacts = createQuery<
  ViewContactsResponse,
  ViewContactsRequest,
  Error
>({
  queryKey: ["viewContacts"],
  staleTime: 1000 * 60,
  fetcher: async (variables) => {
    const { api: viewContactsAPI, ...rest } = variables;
    return api<Omit<ViewContactsRequest, "api">, ViewContactsResponse>({
      method: "GET",
      url: urls[viewContactsAPI],
      requestSchema: ViewContactsRequestVariables,
      responseSchema: GetViewContactsSchema,
      body: rest
    });
  }
});

export const useViewContactsSuspense = createSuspenseQuery<
  ViewContactsResponse,
  ViewContactsRequest,
  Error
>({
  queryKey: ["viewContacts"],
  staleTime: 1000 * 60,
  fetcher: async (variables) => {
    const { api: viewContactsAPI, ...rest } = variables;
    return api<Omit<ViewContactsRequest, "api">, ViewContactsResponse>({
      method: "GET",
      url: urls[viewContactsAPI],
      requestSchema: ViewContactsRequestVariables,
      responseSchema: GetViewContactsSchema,
      body: rest
    });
  }
});
